// src/features/groups/components/GroupCreateModal.jsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Camera, Search } from 'lucide-react';
import useFriends from '../../../hooks/useFriends';
import useGroups from '../hooks/useGroups';
import SelectableFriendItem from '../../../components/SelectableFriendItem';
import { Avatar, AVATAR_VARIANTS } from '../../../components/avatars';
import styles from './GroupCreateModal.module.css';

const GroupCreateModal = ({ onClose, onGroupCreated }) => {
  const { t } = useTranslation();
  const { friends, loading: loadingFriends } = useFriends();
  const { createGroup, creating: isLoading, createError } = useGroups();
  
  const [name, setName] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Update error from hook
  useEffect(() => {
    if (createError) {
      setError(createError);
    }
  }, [createError]);

  useEffect(() => {
    if (iconFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target.result);
      };
      reader.readAsDataURL(iconFile);
    }
  }, [iconFile]);

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setIconFile(file);
    }
  };

  const toggleFriendSelection = (friendId) => {
    setSelectedFriends(prev => {
      if (prev.includes(friendId)) {
        return prev.filter(id => id !== friendId);
      }
      return [...prev, friendId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedFriends.length === 0) {
      setError(t('groups.select_members_error'));
      return;
    }

    setError(null);

    try {
      const newGroup = await createGroup(
        name,
        iconFile,
        selectedFriends
      );
      
      onGroupCreated(newGroup);
    } catch (error) {
      console.error('Error creating group:', error);
      setError(t('groups.create_error'));
    }
  };

  const filteredFriends = friends.filter(friend => {
    const searchTerm = searchQuery.toLowerCase();
    const displayName = (friend.displayName || '').toLowerCase();
    const username = (friend.username || '').toLowerCase();
    return displayName.includes(searchTerm) || username.includes(searchTerm);
  });

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={24} />
          </button>
          <h2>{t('groups.create_group')}</h2>
          <button
            onClick={handleSubmit}
            disabled={!selectedFriends.length || isLoading}
            className={styles.createButton}
          >
            {isLoading ? t('common.creating') : t('groups.create')}
          </button>
        </div>

        {/* Group Info Section */}
        <div className={styles.groupInfo}>
          <div className={styles.iconUploadContainer}>
            <input
              type="file"
              id="group-icon"
              accept="image/*"
              onChange={handleIconChange}
              className={styles.hiddenInput}
              disabled={isLoading}
            />
            <label htmlFor="group-icon" className={styles.iconLabel}>
              {iconPreview ? (
                <Avatar
                  variant={AVATAR_VARIANTS.GROUP}
                  name={name || t('groups.group_icon')}
                  src={iconPreview}
                  size="fluid"
                  alt={t('groups.group_icon')}
                  textColor="var(--background-elevated)"
                  allowUnsafeSrc
                />
              ) : (
                <Camera size={24} className={styles.cameraIcon} />
              )}
            </label>
          </div>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('groups.group_name_placeholder')}
            className={styles.groupNameInput}
            disabled={isLoading}
          />
        </div>

        {/* Search Section */}
        <div className={styles.searchContainer}>
          <div className={styles.searchWrapper}>
            <Search size={20} className={styles.searchIcon} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('groups.search_friends')}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Selected Friends Preview */}
        {selectedFriends.length > 0 && (
          <div className={styles.selectedFriends}>
            {selectedFriends.map(friendId => {
              const friend = friends.find(f => f.id === friendId);
              if (!friend) return null;
              
              return (
                <div key={friendId} className={styles.selectedFriendItem}>
                  <div className={styles.selectedFriendImageContainer}>
                    <Avatar
                      name={friend.displayName || friend.username || t('common.anonymous')}
                      src={friend.photoURL}
                      size="fluid"
                      alt={friend.displayName || friend.username || t('common.anonymous')}
                      textColor="var(--background-elevated)"
                    />
                    <button
                      onClick={() => toggleFriendSelection(friendId)}
                      className={styles.removeButton}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <span className={styles.selectedFriendName}>
                    {friend.displayName || friend.username || t('common.anonymous')}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Friends List */}
        <div className={styles.friendsList}>
          {loadingFriends ? (
            <div className={styles.loadingIndicator}>
              <div className={styles.spinner} />
            </div>
          ) : filteredFriends.length > 0 ? (
            filteredFriends.map(friend => (
              <SelectableFriendItem
                key={friend.id}
                friend={friend}
                isSelected={selectedFriends.includes(friend.id)}
                onToggleSelect={toggleFriendSelection}
                disabled={isLoading}
              />
            ))
          ) : (
            <p className={styles.noFriendsMessage}>
              {searchQuery ? t('groups.no_search_results') : t('groups.no_friends')}
            </p>
          )}
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>
    </div>
  );
};

export default GroupCreateModal;
